import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import Papa from 'papaparse';
import { EmptyState } from '@/components/page-header';
import { VatReportShell, rangeSearch } from '@/components/vat-report';
import { api } from '@/lib/api';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { sourceLabel } from '@/lib/journal';
import { useOrgId } from '@/lib/org';
import { useT } from '@/lib/i18n';
import { downloadCsv, type VatException } from '@/lib/vat';

interface VatExceptionReport {
  orgName: string;
  from: string;
  to: string;
  currency: string;
  rows: VatException[];
  generatedAt: string;
}

export const Route = createFileRoute('/reports/vat-exception')({
  component: VatExceptionReportPage,
  validateSearch: rangeSearch,
});

/** VAT exception (R2 `vat-exception`): the checks of the VAT 201 working over any range. */
function VatExceptionReportPage() {
  const orgId = useOrgId();
  const t = useT();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const q = useQuery({
    queryKey: ['vat', orgId, 'report', 'exceptions', search.from, search.to],
    queryFn: () =>
      api<VatExceptionReport>(
        `/orgs/${orgId}/vat/reports/exceptions?from=${search.from}&to=${search.to}`,
      ),
    enabled: Boolean(orgId),
  });
  const d = q.data;
  const exportCsv = () => {
    if (!d) return;
    const rows = [
      ['Date', 'Transaction type', 'No.', 'Name', 'Check', 'Detail', 'Amount'],
      ...d.rows.map((x) => [
        x.date ? displayDate(x.date) : '',
        sourceLabel(x.sourceType ?? ''),
        x.entryNo ?? '',
        x.contactName ?? '',
        x.title,
        x.message,
        x.amount ?? '',
      ]),
    ];
    downloadCsv(`VAT exceptions ${search.from} to ${search.to}.csv`, Papa.unparse(rows));
  };
  return (
    <VatReportShell
      title={t('reportPages.dashboard.vatReports.exceptionTitle')}
      orgName={d?.orgName}
      from={search.from}
      to={search.to}
      onRun={(r) => void navigate({ search: r })}
      onExport={d ? exportCsv : undefined}
      generatedAt={d?.generatedAt}
    >
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} />
      ) : q.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : q.isError ? (
        <EmptyState
          title={t('reportPages.dashboard.vatReports.loadError')}
          hint={q.error.message}
        />
      ) : d!.rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {t('reportPages.dashboard.vatReports.noExceptions')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                <th className="w-[100px] py-2 text-start font-medium">
                  {t('reportPages.dashboard.vatReports.colDate')}
                </th>
                <th className="py-2 text-start font-medium">
                  {t('reportPages.dashboard.vatReports.colTransaction')}
                </th>
                <th className="py-2 text-start font-medium">
                  {t('reportPages.dashboard.vatReports.colName')}
                </th>
                <th className="py-2 text-start font-medium">
                  {t('reportPages.dashboard.vatReports.colCheck')}
                </th>
                <th className="w-[130px] py-2 text-end font-medium">
                  {t('reportPages.dashboard.vatReports.colAmount')}
                </th>
              </tr>
            </thead>
            <tbody>
              {d!.rows.map((x, i) => (
                <tr key={i} className="border-b border-dashed align-top" data-kind={x.kind}>
                  <td className="py-1.5">{x.date ? displayDate(x.date) : ''}</td>
                  <td className="py-1.5">
                    {sourceLabel(x.sourceType ?? '')}{' '}
                    {x.entryId ? (
                      <Link
                        to="/accounting/journal-entries/$entryId"
                        params={{ entryId: x.entryId }}
                        className="text-primary hover:underline"
                      >
                        {x.entryNo}
                      </Link>
                    ) : null}
                  </td>
                  <td className="py-1.5">{x.contactName ?? ''}</td>
                  <td className="py-1.5">
                    <div className="font-medium">
                      {x.severity === 'error'
                        ? t('reportPages.dashboard.vatReports.errorPrefix')
                        : t('reportPages.dashboard.vatReports.warningPrefix')}{' '}
                      {x.title}
                    </div>
                    <div className="text-muted-foreground">{x.message}</div>
                  </td>
                  <td className="tnum py-1.5 text-end">{x.amount ? formatAmount(x.amount) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </VatReportShell>
  );
}
